import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTours } from '../services/api';
import { MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { tourCurrencySymbol } from '@/lib/utils';
import { TourDateBadge } from '../components/TourDateBadge';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

export default function IstanbulToursPage() {
  const { language } = useLanguage();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const params = { tour_type: 'istanbul', lang: language };
        if (selectedCategory !== 'all') {
          params.category = selectedCategory;
        }
        const response = await getTours(params);
        setTours(response.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, [selectedCategory, language]);

  const categories = [
    { value: 'all', label: 'Tüm Turlar' },
    { value: 'Tarihi Turlar', label: 'Tarihi Turlar' },
    { value: 'Kültürel Turlar', label: 'Kültürel Turlar' },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO fullTitle="Rehberli İstanbul Yürüyüş ve Kültür Turları | İstanbul Turda" description="İstanbul'un tarihini, kültürünü ve eşsiz güzelliklerini profesyonel turist rehberleri eşliğinde keşfedin. İstanbul yürüyüş turlarımızı inceleyin ve hemen rezervasyon yapın." />
      {/* Header */}
      <section className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="istanbul-tours-title">
            İstanbul Turları
          </h1>
          <p className="text-lg text-gray-300">
            Profesyonel rehber Oğuz Keskin eşliğinde İstanbul'un her köşesini keşfedin
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-6">
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                data-testid={`category-${cat.value}`}
                className={`px-6 py-2 rounded-full font-ui font-bold transition-all ${
                  selectedCategory === cat.value
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="h-64 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : tours.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">Bu kategoride henüz tur bulunmamaktadır.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="istanbul-tours-list">
              {tours.map((tour) => (
                <Link
                  to={`/turlar/${tour.slug}`}
                  key={tour.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all hover:-translate-y-1 hover:shadow-xl"
                  data-testid={`tour-item-${tour.slug}`}
                >
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={tour.image_url || 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800'}
                      alt={tour.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800';
                      }}
                    />
                    <TourDateBadge tour={tour} />
                    {tour.discount_percentage > 0 && (
                      <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-ui font-bold">
                        %{tour.discount_percentage} İndirim
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <MapPin size={16} />
                      <span>{tour.category}</span>
                      <span className="text-gray-300">•</span>
                      <Clock size={16} />
                      <span>{tour.duration}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition-colors">
                      {tour.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{tour.short_description}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold font-ui text-orange-500">{tourCurrencySymbol(tour)}{tour.price}</span>
                        <span className="text-sm text-gray-500 ml-1">/kişi</span>
                      </div>
                      <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-ui">
                        Detay
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}